import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { CommunityReviewsIndexParams } from "@circle-react/helpers/urlHelpers/internalApi/admin/communityReviews";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type {
  CommunityReviewPrompt,
  CommunityReviewTagsResponse,
  ReviewRatingBucket,
} from "@circle-react/types/CommunityReview";
import { queryOptions } from "react-query";

// `prompt` decides whether the feed banner and the invitation modal show.
// Average rating and review count are eventually consistent on the server, so
// callers invalidate the prompt after a write instead of patching aggregates.
export const communityReviewKey = createQueryKeyFactory("community-review");

export const communityReviewQueries = {
  prompt: () =>
    queryOptions({
      queryKey: communityReviewKey.key("prompt"),
      queryFn: ({ signal }) =>
        reactQueryGet<CommunityReviewPrompt>(
          internalApi.communityReviews.prompt(),
          { signal },
        ),
    }),
  tags: () =>
    queryOptions({
      queryKey: communityReviewKey.key("tags"),
      queryFn: ({ signal }) =>
        reactQueryGet<CommunityReviewTagsResponse>(
          internalApi.communityReviews.tags(),
          { signal },
        ),
      // Tags are global and rarely change, so keep them for the whole session.
      staleTime: Infinity,
    }),
};

export const communityReviewInvalidations = {
  all: () =>
    queryClient.invalidateQueries({ queryKey: communityReviewKey.root() }),
  prompt: () =>
    queryClient.invalidateQueries({
      queryKey: communityReviewQueries.prompt().queryKey,
    }),
};

export const communityReviewsKey = createQueryKeyFactory("community-reviews");

export interface CommunityReviewTagData {
  key: string;
  label: string;
  rating_bucket: ReviewRatingBucket;
}

export interface CommunityReviewReviewer {
  avatar_url: string | null;
  community_member_id: number | null;
  headline: string | null;
  is_member: boolean;
  name: string;
}

export interface CommunityReviewData {
  can_message: boolean;
  created_at: string;
  description: string | null;
  id: number;
  rating: number;
  reviewer: CommunityReviewReviewer;
  source: string;
  tags: CommunityReviewTagData[];
}

interface CommunityReviewsListPage {
  count: number;
  has_next_page: boolean;
  page: number;
  page_count: number;
  per_page: number;
  records: CommunityReviewData[];
}

interface CommunityReviewsMembersSay {
  generated_at: string | null;
  summary: string | null;
}

export const communityReviewsQueries = {
  list: (params: CommunityReviewsIndexParams) =>
    queryOptions({
      queryKey: communityReviewsKey.key("list", { ...params }),
      queryFn: () =>
        reactQueryGet<CommunityReviewsListPage>(
          internalApi.admin.communityReviews.index(params),
        ),
      placeholderData: (previous?: CommunityReviewsListPage) => previous,
    }),
  membersSay: () =>
    queryOptions({
      queryKey: communityReviewsKey.key("members-say"),
      queryFn: () =>
        reactQueryGet<CommunityReviewsMembersSay>(
          internalApi.admin.communityReviews.membersSay(),
        ),
      staleTime: 5 * 60 * 1000,
    }),
};
